import { useState, useEffect, useCallback } from "react";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Plus, RefreshCw, Rocket, Clock, Calendar, CheckCircle2,
  Target, Users, TrendingUp, BarChart3, Edit2, Trash2,
  Eye, EyeOff, Link2, ChevronRight, ChevronLeft, ChevronUp, ChevronDown, Calendar as CalendarIcon,
  ImageIcon, Upload, Map, Package, Layers,
  Sparkles, Scissors, Palette, Star, Heart, ChefHat, ShoppingBag,
  ClipboardList, Briefcase, Globe, User
} from "lucide-react";

// Import sub-sections
import MarketingSection from "./MarketingSection";
import DefaultsInspoAdminPage from "@/pages/admin/DefaultsInspoAdminPage";
import IntakeFormBuilder from "./IntakeFormBuilder";

const STATUS_COLORS = {
  live: { bg: "bg-green-500", light: "bg-green-50", text: "text-green-700", border: "border-green-200" },
  in_progress: { bg: "bg-blue-500", light: "bg-blue-50", text: "text-blue-700", border: "border-blue-200" },
  planned: { bg: "bg-amber-500", light: "bg-amber-50", text: "text-amber-700", border: "border-amber-200" },
  projected: { bg: "bg-purple-500", light: "bg-purple-50", text: "text-purple-700", border: "border-purple-200" }
};

const STATUS_LABELS = {
  live: "Live",
  in_progress: "In Progress",
  planned: "Planned",
  projected: "Projected"
};

const CATEGORY_ICONS = {
  feature: Rocket,
  marketing: TrendingUp,
  support: Users,
  integration: Link2
};

// Create/Edit Item Dialog
function ItemDialog({ isOpen, onClose, item, onSave }) {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "feature",
    status: "planned",
    priority: 50,
    progress: 0,
    target_date: "",
    visibility: {
      marketing: true,
      features: true,
      team: true,
      investor: true,
      support: true,
      advocate: false
    },
    linked_portals: [],
    tags: []
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleImageUpload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folder", "roadmap");
      const res = await fetch(`${API}/pilot/upload-image`, { method: "POST", body: fd });
      if (res.ok) {
        const data = await res.json();
        setFormData(prev => ({ ...prev, image_url: data.url }));
        toast.success("Image uploaded");
      } else {
        toast.error("Upload failed");
      }
    } catch { toast.error("Upload error"); }
    finally { setUploading(false); }
  };

  useEffect(() => {
    if (item) {
      setFormData({ ...formData, ...item });
    } else {
      setFormData({
        title: "",
        description: "",
        category: "feature",
        status: "planned",
        priority: 50,
        progress: 0,
        target_date: "",
        visibility: {
          marketing: true,
          features: true,
          team: true,
          investor: true,
          support: true,
          advocate: false
        },
        linked_portals: [],
        tags: []
      });
    }
  }, [item, isOpen]);

  const handleSubmit = async () => {
    if (!formData.title) {
      toast.error("Title is required");
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem("session_token");
      const url = item 
        ? `${API}/roadmap/items/${item.item_id}`
        : `${API}/roadmap/items`;
      
      const res = await fetch(url, {
        method: item ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        toast.success(item ? "Item updated" : "Item created");
        onSave();
        onClose();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to save");
      }
    } catch (err) {
      toast.error("Error saving item");
    } finally {
      setSaving(false);
    }
  };

  const toggleVisibility = (key) => {
    setFormData(prev => ({
      ...prev,
      visibility: { ...prev.visibility, [key]: !prev.visibility[key] }
    }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{item ? "Edit Roadmap Item" : "Add Roadmap Item"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <Label>Title *</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Feature or milestone name"
            />
          </div>

          <div>
            <Label>Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="What does this accomplish?"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Category</Label>
              <Select 
                value={formData.category} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, category: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="feature">Feature</SelectItem>
                  <SelectItem value="marketing">Marketing</SelectItem>
                  <SelectItem value="support">Support</SelectItem>
                  <SelectItem value="integration">Integration</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select 
                value={formData.status} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, status: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="projected">Projected</SelectItem>
                  <SelectItem value="planned">Planned</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="live">Live</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Priority (1-100)</Label>
              <Input
                type="number"
                min="1"
                max="100"
                value={formData.priority}
                onChange={(e) => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) }))}
              />
            </div>
            <div>
              <Label>Progress (%)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                value={formData.progress}
                onChange={(e) => setFormData(prev => ({ ...prev, progress: parseInt(e.target.value) }))}
              />
            </div>
          </div>

          <div>
            <Label>Target Date</Label>
            <Input
              type="date"
              value={formData.target_date?.split("T")[0] || ""}
              onChange={(e) => setFormData(prev => ({ ...prev, target_date: e.target.value }))}
            />
          </div>

          {/* Image Upload */}
          <div>
            <Label>Attached Image</Label>
            {formData.image_url ? (
              <div className="mt-2 relative group">
                <img src={formData.image_url} alt="Event" className="w-full h-32 object-cover rounded-lg border" />
                <button
                  onClick={() => setFormData(prev => ({ ...prev, image_url: "" }))}
                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                >x</button>
              </div>
            ) : (
              <label className="mt-2 flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                {uploading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 text-gray-400" />}
                <span className="text-sm text-gray-500">{uploading ? "Uploading..." : "Click to upload image"}</span>
                <input type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files?.[0]) handleImageUpload(e.target.files[0]); }} />
              </label>
            )}
          </div>

          <div>
            <Label>Visibility</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {Object.entries(formData.visibility).map(([key, visible]) => (
                <button
                  key={key}
                  onClick={() => toggleVisibility(key)}
                  className={`px-3 py-1 rounded-full text-sm flex items-center gap-1 transition-all ${
                    visible 
                      ? "bg-green-100 text-green-700 border border-green-300"
                      : "bg-gray-100 text-gray-500 border border-gray-200"
                  }`}
                >
                  {visible ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                  {key.charAt(0).toUpperCase() + key.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? "Saving..." : item ? "Update" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function RoadmapSection() {
  const [items, setItems] = useState([]);
  const [grouped, setGrouped] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState("all");
  const [showDialog, setShowDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/roadmap/items?view=${activeView}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setItems(data.items || []);
        setGrouped(data.grouped || {});
      }
    } catch (err) {
      console.error("Failed to fetch roadmap:", err);
    } finally {
      setLoading(false);
    }
  }, [activeView]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleDelete = async (itemId) => {
    if (!window.confirm("Delete this roadmap item?")) return;

    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/roadmap/items/${itemId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success("Item deleted");
        fetchItems();
      }
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setShowDialog(true);
  };

  const openCreate = () => {
    setEditingItem(null);
    setShowDialog(true);
  };

  // Main section tab state
  const [mainSection, setMainSection] = useState("roadmap");

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Main Section Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Roadmap & Planning</h2>
          <p className="text-gray-500">Manage roadmap, marketing, product features, and defaults</p>
        </div>
      </div>

      {/* Main Section Tabs */}
      <Tabs value={mainSection} onValueChange={setMainSection}>
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="roadmap" className="flex items-center gap-2">
            <Map className="w-4 h-4" />
            Roadmap
          </TabsTrigger>
          <TabsTrigger value="marketing" className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Marketing
          </TabsTrigger>
          <TabsTrigger value="product" className="flex items-center gap-2">
            <Package className="w-4 h-4" />
            Product
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Section Content */}
      {mainSection === "roadmap" && (
        <RoadmapContent 
          items={items}
          grouped={grouped}
          loading={loading}
          showCalendar={showCalendar}
          setShowCalendar={setShowCalendar}
          currentMonth={currentMonth}
          setCurrentMonth={setCurrentMonth}
          activeView={activeView}
          setActiveView={setActiveView}
          openCreate={openCreate}
          openEdit={openEdit}
          handleDelete={handleDelete}
        />
      )}

      {mainSection === "marketing" && (
        <MarketingSection />
      )}

      {mainSection === "product" && (
        <ProductSection items={items} />
      )}

      {/* Item Dialog - shared */}
      <ItemDialog 
        isOpen={showDialog} 
        onClose={() => setShowDialog(false)} 
        item={editingItem} 
        onSave={fetchItems} 
      />
    </div>
  );
}

// Product Section - contains Features, Defaults & Inspo, and Private Portal as sub-tabs
function ProductSection({ items }) {
  const [productTab, setProductTab] = useState("features");
  
  return (
    <div className="space-y-6">
      <Tabs value={productTab} onValueChange={setProductTab}>
        <TabsList>
          <TabsTrigger value="features">Features</TabsTrigger>
          <TabsTrigger value="defaults">Defaults & Inspo</TabsTrigger>
          <TabsTrigger value="portals">Private Portal</TabsTrigger>
        </TabsList>
      </Tabs>
      
      {productTab === "features" && (
        <ProductFeaturesSection items={items} />
      )}
      
      {productTab === "defaults" && (
        <DefaultsInspoAdminPage />
      )}
      
      {productTab === "portals" && (
        <PrivatePortalSection />
      )}
    </div>
  );
}

// Product Features Section - shows categorized feature list
function ProductFeaturesSection({ items }) {
  const features = items.filter(i => i.category === "feature");
  const byStatus = {
    live: features.filter(i => i.status === "live"),
    in_progress: features.filter(i => i.status === "in_progress"),
    planned: features.filter(i => i.status === "planned"),
    projected: features.filter(i => i.status === "projected")
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Product Features</h3>
        <Badge variant="outline">{features.length} total features</Badge>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {Object.entries(byStatus).map(([status, statusFeatures]) => (
          <Card key={status} className="border-t-4" style={{ borderTopColor: STATUS_COLORS[status]?.bg?.replace('bg-', '#').replace('-500', '') || '#ccc' }}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium capitalize flex items-center justify-between">
                {status.replace('_', ' ')}
                <Badge variant="secondary">{statusFeatures.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {statusFeatures.length === 0 ? (
                <p className="text-xs text-gray-400 italic">No features</p>
              ) : (
                statusFeatures.map(feature => (
                  <div key={feature.item_id} className="p-2 bg-gray-50 rounded text-sm">
                    <p className="font-medium">{feature.title}</p>
                    {feature.description && (
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2">{feature.description}</p>
                    )}
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// Roadmap Content - the original roadmap view
function RoadmapContent({ 
  items, grouped, loading, showCalendar, setShowCalendar, 
  currentMonth, setCurrentMonth, activeView, setActiveView,
  openCreate, openEdit, handleDelete 
}) {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Product Roadmap</h3>
          <p className="text-gray-500 text-sm">Track features, milestones, and projections</p>
        </div>
        <div className="flex gap-2">
          <Button variant={showCalendar ? "outline" : "default"} onClick={() => setShowCalendar(false)} size="sm">
            Board View
          </Button>
          <Button variant={showCalendar ? "default" : "outline"} onClick={() => setShowCalendar(true)} size="sm">
            <CalendarIcon className="w-4 h-4 mr-1" />
            Calendar
          </Button>
          <Button onClick={openCreate}>
            <Plus className="w-4 h-4 mr-2" />
            Add Item
          </Button>
        </div>
      </div>

      {/* View Tabs */}
      <Tabs value={activeView} onValueChange={setActiveView}>
        <TabsList>
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="features">Features</TabsTrigger>
          <TabsTrigger value="marketing">Marketing</TabsTrigger>
          <TabsTrigger value="team">Team</TabsTrigger>
          <TabsTrigger value="investor">Investor</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Calendar View */}
      {showCalendar ? (
        <RoadmapCalendar 
          items={items} 
          currentMonth={currentMonth}
          setCurrentMonth={setCurrentMonth}
          onEdit={openEdit}
          statusColors={STATUS_COLORS}
        />
      ) : (
      <>
      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        {Object.entries(STATUS_LABELS).map(([status, label]) => {
          const count = grouped[status]?.length || 0;
          const colors = STATUS_COLORS[status];
          return (
            <Card key={status} className={`${colors.light} ${colors.border} border`}>
              <CardContent className="pt-4 pb-4 text-center">
                <p className={`text-2xl font-bold ${colors.text}`}>{count}</p>
                <p className="text-sm text-gray-600">{label}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Items by Status */}
      <div className="grid md:grid-cols-4 gap-4">
        {Object.entries(STATUS_LABELS).map(([status, label]) => {
          const statusItems = grouped[status] || [];
          const colors = STATUS_COLORS[status];
          
          return (
            <div key={status} className="space-y-2">
              <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${colors.light}`}>
                <div className={`w-2 h-2 rounded-full ${colors.bg}`} />
                <span className={`font-medium text-sm ${colors.text}`}>{label}</span>
                <Badge variant="secondary" className="ml-auto">{statusItems.length}</Badge>
              </div>
              
              <div className="space-y-2">
                {statusItems.map(item => {
                  const Icon = CATEGORY_ICONS[item.category] || Rocket;
                  return (
                    <Card key={item.item_id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-3">
                        {item.image_url && (
                          <img src={item.image_url} alt={item.title} className="w-full h-20 object-cover rounded-md mb-2" />
                        )}
                        <div className="flex items-start gap-2">
                          <div className={`w-8 h-8 rounded ${colors.light} ${colors.text} flex items-center justify-center shrink-0`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{item.title}</p>
                            {item.description && (
                              <p className="text-xs text-gray-500 line-clamp-2 mt-1">{item.description}</p>
                            )}
                            {item.progress > 0 && item.progress < 100 && (
                              <div className="mt-2">
                                <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
                                  <div 
                                    className={`h-full ${colors.bg}`}
                                    style={{ width: `${item.progress}%` }}
                                  />
                                </div>
                                <span className="text-xs text-gray-400">{item.progress}%</span>
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col gap-1">
                            <button 
                              onClick={() => openEdit(item)}
                              className="p-1 hover:bg-gray-100 rounded"
                            >
                              <Edit2 className="w-3 h-3 text-gray-400" />
                            </button>
                            <button 
                              onClick={() => handleDelete(item.item_id)}
                              className="p-1 hover:bg-red-50 rounded"
                            >
                              <Trash2 className="w-3 h-3 text-red-400" />
                            </button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
                
                {statusItems.length === 0 && (
                  <div className="text-center py-4 text-gray-400 text-sm">
                    No items
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      </>
      )}
    </div>
  );
}


function RoadmapCalendar({ items, currentMonth, setCurrentMonth, onEdit, statusColors }) {
  const getDaysInMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const getFirstDayOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  const monthName = currentMonth.toLocaleString("default", { month: "long", year: "numeric" });

  const getItemsForDay = (day) => {
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return items.filter(item => {
      if (!item.target_date) return false;
      return item.target_date.startsWith(dateStr);
    });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>{monthName}</CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" size="icon" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setCurrentMonth(new Date())}>Today</Button>
            <Button variant="outline" size="icon" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-7 gap-px bg-gray-200 rounded-lg overflow-hidden">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(d => (
            <div key={d} className="bg-gray-50 p-2 text-center text-xs font-medium text-gray-500">{d}</div>
          ))}
          {Array.from({ length: getFirstDayOfMonth(currentMonth) }).map((_, i) => (
            <div key={`empty-${i}`} className="bg-white p-2 min-h-[80px]" />
          ))}
          {Array.from({ length: getDaysInMonth(currentMonth) }).map((_, i) => {
            const day = i + 1;
            const dayItems = getItemsForDay(day);
            const isToday = new Date().getDate() === day && new Date().getMonth() === currentMonth.getMonth() && new Date().getFullYear() === currentMonth.getFullYear();
            return (
              <div key={day} className={`bg-white p-2 min-h-[80px] ${isToday ? "ring-2 ring-purple-500 ring-inset" : ""}`}>
                <span className={`text-xs font-medium ${isToday ? "text-purple-600" : "text-gray-600"}`}>{day}</span>
                <div className="mt-1 space-y-1">
                  {dayItems.slice(0, 2).map(item => {
                    const colors = statusColors[item.status] || statusColors.projected;
                    return (
                      <div
                        key={item.item_id}
                        className={`text-xs px-1 py-0.5 rounded cursor-pointer ${colors.light} ${colors.text} flex items-center gap-1`}
                        onClick={() => onEdit(item)}
                        title={item.title}
                      >
                        {item.image_url && (
                          <img src={item.image_url} alt="" className="w-4 h-4 rounded object-cover shrink-0" />
                        )}
                        <span className="truncate">{item.title}</span>
                      </div>
                    );
                  })}
                  {dayItems.length > 2 && <span className="text-xs text-gray-400">+{dayItems.length - 2} more</span>}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}



// Private Portal Section - Edit pilot presets and host dashboards for all categories
// This is the SINGLE SOURCE OF TRUTH for each category - syncs to demo pages, invites, host dashboards
function PrivatePortalSection() {
  const [activePortalType, setActivePortalType] = useState("pilots");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [dashboardTemplates, setDashboardTemplates] = useState([]);
  const [intakeTemplate, setIntakeTemplate] = useState(null);
  const [defaultsInspo, setDefaultsInspo] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Platform-wide defaults and inspo from Defaults & Inspo admin page
  const [platformDefaults, setPlatformDefaults] = useState({});
  const [platformInspo, setPlatformInspo] = useState([]);
  const [defaultCategories, setDefaultCategories] = useState([]);
  
  // Category metadata for displaying icons/names
  // Note: builder_types is excluded as it's an intake question, NOT a platform default
  const CATEGORY_METADATA = {
    dietary_options: { name: "Dietary", iconName: "Utensils", description: "Food preferences & restrictions" },
    accessibility_options: { name: "Accessibility", iconName: "Accessibility", description: "Accessibility needs & accommodations" },
    interests: { name: "Interests", iconName: "Heart", description: "Industries, hobbies & passions" },
    audience_types: { name: "Audience", iconName: "Users", description: "Learning preferences" },
    industries: { name: "Industries", iconName: "Building2", description: "Industry sectors" },
    vibe_options: { name: "Vibe", iconName: "Music", description: "Event atmosphere preferences" },
    hair_options: { name: "Hair", iconName: "Scissors", description: "Hair type, length & preferences" },
    nails_options: { name: "Nails", iconName: "Sparkles", description: "Nail shape, length & style" },
    face_options: { name: "Face", iconName: "Palette", description: "Makeup preferences & skin info" },
    lashes_options: { name: "Lashes", iconName: "Eye", description: "Lash preferences & style" },
    travel_options: { name: "Travel", iconName: "Plane", description: "Transportation & lodging preferences" },
    wellness_options: { name: "Wellness", iconName: "Leaf", description: "Wellness & self-care preferences" },
    // Retail & Fashion categories
    style_options: { name: "Style", iconName: "Shirt", description: "Fashion aesthetics & style preferences" },
    shoe_options: { name: "Shoes", iconName: "Footprints", description: "Footwear preferences" },
    jewelry_options: { name: "Jewelry", iconName: "Gem", description: "Jewelry & diamond preferences" },
    metal_options: { name: "Metals", iconName: "Settings", description: "Metal & material preferences" },
    sizes: { name: "Sizes", iconName: "Ruler", description: "Clothing, shoe sizes" }
  };
  
  // Categories to exclude from defaults display (these are intake questions, not platform defaults)
  const EXCLUDED_DEFAULT_CATEGORIES = ['builder_types'];
  
  // Icon mapping to Lucide components
  const ICON_MAP = {
    sparkles: Sparkles,
    scissors: Scissors,
    palette: Palette,
    star: Star,
    heart: Heart,
    chef: ChefHat,
    "shopping-bag": ShoppingBag,
    clipboard: ClipboardList,
    users: Users,
    briefcase: Briefcase,
    globe: Globe,
    user: User,
    eye: Eye
  };
  
  // Pilot categories with proper Lucide icons - SINGLE SOURCE OF TRUTH
  const pilotCategories = [
    { id: "makeup", name: "Makeup Artists", icon: "sparkles", color: "#ec4899", demoPath: "/mua" },
    { id: "hair", name: "Hair Stylists", icon: "scissors", color: "#8b5cf6", demoPath: "/hair" },
    { id: "nail", name: "Nail Technicians", icon: "palette", color: "#f43f5e", demoPath: "/nail" },
    { id: "lash", name: "Lash Artists", icon: "star", color: "#a855f7", demoPath: "/lash" },
    { id: "wellness", name: "Wellness Professionals", icon: "heart", color: "#10b981", demoPath: "/wellness" },
    { id: "culinary", name: "Culinary Professionals", icon: "chef", color: "#f97316", demoPath: "/culinary" },
    { id: "style", name: "Retail & Fashion", icon: "shopping-bag", color: "#6366f1", demoPath: "/retail" },
    { id: "instructor", name: "Instructors & Educators", icon: "clipboard", color: "#0ea5e9", demoPath: "/instructor" },
    { id: "events", name: "Events & Experiences", icon: "users", color: "#3b82f6", demoPath: "/events" }
  ];
  
  // Support categories
  const supportCategories = [
    { id: "team", name: "Team Portal", icon: "users", color: "#3b82f6", demoPath: null },
    { id: "investor", name: "Investor Portal", icon: "briefcase", color: "#10b981", demoPath: null },
    { id: "network", name: "Network Portal", icon: "globe", color: "#8b5cf6", demoPath: null },
    { id: "client", name: "Client Portal", icon: "user", color: "#f97316", demoPath: null },
    { id: "public", name: "Public Portal", icon: "eye", color: "#ec4899", demoPath: null }
  ];
  
  // Helper to get icon component
  const getIconComponent = (iconName) => {
    return ICON_MAP[iconName] || Package;
  };
  
  const categories = activePortalType === "pilots" ? pilotCategories : supportCategories;
  const selectedCategoryData = categories.find(c => c.id === selectedCategory);
  
  // Fetch templates
  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/portal-templates`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTemplates(data.templates || []);
      }
    } catch (err) {
      console.error("Failed to fetch templates:", err);
    }
    
    // Fetch dashboard templates
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/dashboard-templates`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDashboardTemplates(data.templates || []);
      }
    } catch (err) {
      console.error("Failed to fetch dashboard templates:", err);
    }
    
    // Fetch ALL platform defaults from Defaults & Inspo admin page
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/platform-defaults/field-options`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPlatformDefaults(data.field_options || {});
        setDefaultCategories(data.categories || []);
      }
    } catch (err) {
      console.error("Failed to fetch platform defaults:", err);
    }
    
    // Fetch ALL platform inspo
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/platform-defaults/inspo`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPlatformInspo(data.inspo || []);
      }
    } catch (err) {
      console.error("Failed to fetch platform inspo:", err);
    }
    
    setLoading(false);
  };
  
  // Fetch intake template for selected category
  const fetchCategoryIntake = async (categoryId) => {
    if (!categoryId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      // Fetch intake template
      const intakeRes = await fetch(`${API}/admin/intake-templates/${categoryId}`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (intakeRes.ok) {
        const data = await intakeRes.json();
        setIntakeTemplate(data.template || null);
      }
      
      // Fetch defaults/inspo for this category
      const defaultsRes = await fetch(`${API}/admin/category-defaults/${categoryId}`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (defaultsRes.ok) {
        const data = await defaultsRes.json();
        setDefaultsInspo(data.defaults || {});
      }
    } catch (err) {
      console.error("Failed to fetch category intake:", err);
    }
    setLoading(false);
  };
  
  useEffect(() => {
    fetchTemplates();
  }, []);
  
  // Fetch intake when category changes
  useEffect(() => {
    if (selectedCategory) {
      fetchCategoryIntake(selectedCategory);
    }
  }, [selectedCategory]);
  
  const getCategoryTemplate = (categoryId) => {
    return templates.find(t => t.template_id === categoryId);
  };
  
  const getCategoryDashboard = (categoryId) => {
    return dashboardTemplates.find(t => t.category === categoryId);
  };
  
  // Save intake questions
  const handleSaveIntake = async (questions) => {
    setSaving(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/intake-templates/${selectedCategory}`, {
        method: "PUT",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ questions, category: selectedCategory })
      });
      if (res.ok) {
        toast.success("Intake form saved - syncing to all portals");
        fetchCategoryIntake(selectedCategory);
      } else {
        toast.error("Failed to save intake form");
      }
    } catch (err) {
      toast.error("Error saving intake form");
    }
    setSaving(false);
  };
  
  // Save defaults/inspo
  const handleSaveDefaults = async (defaults) => {
    setSaving(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/category-defaults/${selectedCategory}`, {
        method: "PUT",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ defaults, category: selectedCategory })
      });
      if (res.ok) {
        toast.success("Defaults saved - syncing to host dashboards");
        setDefaultsInspo(defaults);
      } else {
        toast.error("Failed to save defaults");
      }
    } catch (err) {
      toast.error("Error saving defaults");
    }
    setSaving(false);
  };
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Private Portal Templates</h3>
          <p className="text-sm text-gray-500">
            Edit default presets for pilot onboarding and host dashboards. These settings sync as defaults that can be overridden per invite.
          </p>
        </div>
        <Button variant="outline" onClick={fetchTemplates} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>
      
      {/* Portal Type Tabs */}
      <Tabs value={activePortalType} onValueChange={setActivePortalType}>
        <TabsList>
          <TabsTrigger value="pilots">Pilot Portals</TabsTrigger>
          <TabsTrigger value="support">Support Portals</TabsTrigger>
        </TabsList>
      </Tabs>
      
      {/* Category Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {categories.map(cat => {
          const template = getCategoryTemplate(cat.id);
          const dashboard = getCategoryDashboard(cat.id);
          const hasTemplate = !!template;
          const hasDashboard = !!dashboard;
          const IconComponent = getIconComponent(cat.icon);
          
          return (
            <Card 
              key={cat.id} 
              className={`cursor-pointer hover:shadow-md transition-all border-2 ${
                selectedCategory === cat.id ? 'border-purple-500 ring-2 ring-purple-200' : 'border-gray-200'
              }`}
              onClick={() => setSelectedCategory(selectedCategory === cat.id ? null : cat.id)}
            >
              <CardContent className="p-4 text-center">
                <div 
                  className="w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center"
                  style={{ backgroundColor: cat.color + '20' }}
                >
                  <IconComponent className="w-6 h-6" style={{ color: cat.color }} />
                </div>
                <h4 className="font-medium text-sm">{cat.name}</h4>
                <div className="flex gap-1 justify-center mt-2">
                  <Badge variant={hasTemplate ? "default" : "outline"} className="text-xs">
                    {hasTemplate ? "Preset ✓" : "No Preset"}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      
      {/* Selected Category Editor - FULL INTAKE FORM BUILDER */}
      {selectedCategory && selectedCategoryData && (
        <Card className="border-2" style={{ borderColor: selectedCategoryData.color }}>
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {(() => {
                  const IconComp = getIconComponent(selectedCategoryData.icon);
                  return (
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: selectedCategoryData.color + '20' }}
                    >
                      <IconComp className="w-5 h-5" style={{ color: selectedCategoryData.color }} />
                    </div>
                  );
                })()}
                <div>
                  <span className="text-lg">{selectedCategoryData.name}</span>
                  <p className="text-sm text-gray-500 font-normal">Single source of truth - all changes sync to portals</p>
                </div>
              </div>
              <div className="flex gap-2">
                {selectedCategoryData.demoPath && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => window.open(selectedCategoryData.demoPath, '_blank')}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    View Demo
                  </Button>
                )}
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedCategory(null)}
                >
                  Close
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <Tabs defaultValue="onboarding">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="onboarding">Pilot Onboarding</TabsTrigger>
                <TabsTrigger value="dashboard">Host Dashboard & Defaults</TabsTrigger>
              </TabsList>
              
              {/* PILOT ONBOARDING TAB - Full Intake Form Builder */}
              <TabsContent value="onboarding" className="space-y-4 mt-4">
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-purple-800 mb-1">Intake Form Builder</h4>
                  <p className="text-sm text-purple-700">
                    Edit all intake questions for {selectedCategoryData.name}. Changes sync to all new invites. 
                    Individual invites can override specific fields.
                  </p>
                </div>
                
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
                    <span className="ml-2 text-gray-500">Loading intake template...</span>
                  </div>
                ) : (
                  <IntakeFormBuilder
                    intakeQuestions={intakeTemplate?.questions || []}
                    defaultsInspo={defaultsInspo}
                    onIntakeQuestionsChange={(questions) => {
                      setIntakeTemplate(prev => ({ ...prev, questions }));
                    }}
                    onDefaultsInspoChange={setDefaultsInspo}
                    pilotCategory={selectedCategory}
                  />
                )}
                
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button 
                    variant="outline" 
                    onClick={() => fetchCategoryIntake(selectedCategory)}
                    disabled={loading}
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                    Reset
                  </Button>
                  <Button 
                    onClick={() => handleSaveIntake(intakeTemplate?.questions || [])}
                    disabled={saving}
                    style={{ backgroundColor: selectedCategoryData.color }}
                  >
                    {saving ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Save & Sync to Portals
                      </>
                    )}
                  </Button>
                </div>
              </TabsContent>
              
              {/* HOST DASHBOARD TAB - Defaults & Inspo */}
              <TabsContent value="dashboard" className="space-y-4 mt-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-blue-800 mb-1">Host Dashboard Defaults</h4>
                  <p className="text-sm text-blue-700">
                    Configure what defaults and resources appear in host dashboards for {selectedCategoryData.name}.
                    Hosts can toggle these on/off. Each pilot can see custom labels.
                  </p>
                </div>
                
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
                    <span className="ml-2 text-gray-500">Loading defaults...</span>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Section Label Editor */}
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base">Section Labels</CardTitle>
                        <p className="text-sm text-gray-500">Customize how sections are named for this category. Each pilot can override.</p>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-sm">Inspiration Section Label</Label>
                            <Input 
                              value={defaultsInspo.inspoLabel || "Inspiration"} 
                              onChange={(e) => setDefaultsInspo(prev => ({ ...prev, inspoLabel: e.target.value }))}
                              placeholder="e.g., Resources, Inspo, Portfolio..."
                            />
                            <p className="text-xs text-gray-400 mt-1">For instructors, this might be "Resources"</p>
                          </div>
                          <div>
                            <Label className="text-sm">Defaults Section Label</Label>
                            <Input 
                              value={defaultsInspo.defaultsLabel || "Defaults"} 
                              onChange={(e) => setDefaultsInspo(prev => ({ ...prev, defaultsLabel: e.target.value }))}
                              placeholder="e.g., Preferences, Settings..."
                            />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    {/* Defaults Toggle List - FROM PLATFORM DEFAULTS */}
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base">Available Defaults from Platform</CardTitle>
                        <p className="text-sm text-gray-500">Toggle which default categories are enabled for {selectedCategoryData?.name}. Synced from Defaults & Inspo admin page.</p>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {defaultCategories.length === 0 ? (
                          <div className="text-center py-4 text-gray-400">
                            <p>Loading default categories...</p>
                          </div>
                        ) : (
                          defaultCategories.filter(cat => !EXCLUDED_DEFAULT_CATEGORIES.includes(cat)).map(categoryKey => {
                            const meta = CATEGORY_METADATA[categoryKey] || { name: categoryKey, icon: "📦", description: "" };
                            const options = platformDefaults[categoryKey] || [];
                            const enabledCount = options.filter(o => o.enabled !== false && o.status === "published").length;
                            const isExpanded = defaultsInspo.expandedCategories?.[categoryKey];
                            const isEnabled = defaultsInspo.enabledDefaults?.[categoryKey] !== false;
                            
                            return (
                              <div key={categoryKey} className="border rounded-lg overflow-hidden">
                                {/* Category Header */}
                                <div 
                                  className={`flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 ${isEnabled ? 'bg-white' : 'bg-gray-100'}`}
                                  onClick={() => setDefaultsInspo(prev => ({
                                    ...prev,
                                    expandedCategories: { 
                                      ...prev.expandedCategories, 
                                      [categoryKey]: !prev.expandedCategories?.[categoryKey] 
                                    }
                                  }))}
                                >
                                  <div className="flex items-center gap-3">
                                    <span className="text-xl">{meta.icon}</span>
                                    <div>
                                      <span className="font-medium text-sm">{meta.name}</span>
                                      <p className="text-xs text-gray-500">{meta.description}</p>
                                    </div>
                                    <Badge variant="outline" className="text-xs ml-2">{enabledCount} options</Badge>
                                  </div>
                                  <div className="flex items-center gap-3">
                                    {/* Enable/Disable entire category */}
                                    <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                                      <span className="text-xs text-gray-500">Enable</span>
                                      <input
                                        type="checkbox"
                                        checked={isEnabled}
                                        onChange={(e) => setDefaultsInspo(prev => ({
                                          ...prev,
                                          enabledDefaults: { ...prev.enabledDefaults, [categoryKey]: e.target.checked }
                                        }))}
                                        className="w-5 h-5 rounded border-gray-300"
                                      />
                                    </div>
                                    {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                  </div>
                                </div>
                                
                                {/* Category Options - Expandable */}
                                {isExpanded && isEnabled && (
                                  <div className="p-3 bg-gray-50 border-t space-y-2 max-h-60 overflow-y-auto">
                                    {options.map(option => (
                                      <div key={option.id} className="flex items-center justify-between py-1 px-2 bg-white rounded text-sm">
                                        <div className="flex-1">
                                          <span className="font-medium">{option.label}</span>
                                          {option.description && (
                                            <span className="text-gray-400 ml-2 text-xs">{option.description}</span>
                                          )}
                                        </div>
                                        <Badge variant={option.status === "published" ? "default" : "secondary"} className="text-xs">
                                          {option.status || "draft"}
                                        </Badge>
                                      </div>
                                    ))}
                                    {options.length === 0 && (
                                      <p className="text-xs text-gray-400 text-center py-2">No options in this category</p>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })
                        )}
                      </CardContent>
                    </Card>
                    
                    {/* Inspiration/Resources from Platform */}
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center justify-between">
                          <span>{defaultsInspo.inspoLabel || "Inspiration"} from Platform ({platformInspo.length} items)</span>
                        </CardTitle>
                        <p className="text-sm text-gray-500">Toggle which platform inspiration items are enabled for this category</p>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {platformInspo.length === 0 ? (
                          <div className="text-center py-8 text-gray-400">
                            <Layers className="w-8 h-8 mx-auto mb-2" />
                            <p>No platform inspiration items yet</p>
                            <p className="text-xs">Add items in Defaults & Inspo admin page</p>
                          </div>
                        ) : (
                          platformInspo.map((inspo) => (
                            <div key={inspo.inspo_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-sm">{inspo.title}</span>
                                  <Badge variant="outline" className="text-xs">{inspo.type || "link"}</Badge>
                                </div>
                                {inspo.description && (
                                  <p className="text-xs text-gray-500 mt-1">{inspo.description}</p>
                                )}
                              </div>
                              <input
                                type="checkbox"
                                checked={defaultsInspo.enabledInspo?.[inspo.inspo_id] !== false}
                                onChange={(e) => setDefaultsInspo(prev => ({
                                  ...prev,
                                  enabledInspo: { ...prev.enabledInspo, [inspo.inspo_id]: e.target.checked }
                                }))}
                                className="w-5 h-5 rounded border-gray-300 ml-3"
                              />
                            </div>
                          ))
                        )}
                      </CardContent>
                    </Card>
                    
                    {/* Custom Resources for this category */}
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center justify-between">
                          <span>{defaultsInspo.inspoLabel || "Inspiration"} Items</span>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => setDefaultsInspo(prev => ({
                              ...prev,
                              resources: [...(prev.resources || []), { 
                                id: `resource_${Date.now()}`, 
                                title: "", 
                                description: "", 
                                link: "",
                                type: "link"
                              }]
                            }))}
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Add Item
                          </Button>
                        </CardTitle>
                        <p className="text-sm text-gray-500">Add resources, inspiration links, or documents for this category</p>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {(defaultsInspo.resources || []).length === 0 ? (
                          <div className="text-center py-8 text-gray-400">
                            <Layers className="w-8 h-8 mx-auto mb-2" />
                            <p>No {(defaultsInspo.inspoLabel || "inspiration").toLowerCase()} items yet</p>
                            <p className="text-xs">Click "Add Item" to create one</p>
                          </div>
                        ) : (
                          (defaultsInspo.resources || []).map((resource, idx) => (
                            <div key={resource.id || idx} className="p-4 bg-gray-50 rounded-lg border space-y-3">
                              <div className="flex items-start justify-between">
                                <div className="flex-1 space-y-2">
                                  <Input
                                    value={resource.title || ""}
                                    onChange={(e) => {
                                      const updated = [...(defaultsInspo.resources || [])];
                                      updated[idx] = { ...updated[idx], title: e.target.value };
                                      setDefaultsInspo(prev => ({ ...prev, resources: updated }));
                                    }}
                                    placeholder="Title (e.g., Portfolio Guide)"
                                    className="font-medium"
                                  />
                                  <Input
                                    value={resource.link || ""}
                                    onChange={(e) => {
                                      const updated = [...(defaultsInspo.resources || [])];
                                      updated[idx] = { ...updated[idx], link: e.target.value };
                                      setDefaultsInspo(prev => ({ ...prev, resources: updated }));
                                    }}
                                    placeholder="URL (https://...)"
                                  />
                                  <textarea
                                    value={resource.description || ""}
                                    onChange={(e) => {
                                      const updated = [...(defaultsInspo.resources || [])];
                                      updated[idx] = { ...updated[idx], description: e.target.value };
                                      setDefaultsInspo(prev => ({ ...prev, resources: updated }));
                                    }}
                                    placeholder="Description (optional)"
                                    className="w-full text-sm p-2 border rounded-md"
                                    rows={2}
                                  />
                                </div>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-500 hover:bg-red-50 ml-2"
                                  onClick={() => {
                                    const updated = (defaultsInspo.resources || []).filter((_, i) => i !== idx);
                                    setDefaultsInspo(prev => ({ ...prev, resources: updated }));
                                  }}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                              <div className="flex gap-2">
                                <select
                                  value={resource.type || "link"}
                                  onChange={(e) => {
                                    const updated = [...(defaultsInspo.resources || [])];
                                    updated[idx] = { ...updated[idx], type: e.target.value };
                                    setDefaultsInspo(prev => ({ ...prev, resources: updated }));
                                  }}
                                  className="text-xs border rounded p-1"
                                >
                                  <option value="link">Link</option>
                                  <option value="video">Video</option>
                                  <option value="document">Document</option>
                                  <option value="image">Image</option>
                                </select>
                              </div>
                            </div>
                          ))
                        )}
                      </CardContent>
                    </Card>
                  </div>
                )}
                
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button 
                    variant="outline" 
                    onClick={() => fetchCategoryIntake(selectedCategory)}
                    disabled={loading}
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                    Reset
                  </Button>
                  <Button 
                    onClick={() => handleSaveDefaults(defaultsInspo)}
                    disabled={saving}
                    style={{ backgroundColor: selectedCategoryData.color }}
                  >
                    {saving ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Save Defaults
                      </>
                    )}
                  </Button>
                </div>
              </TabsContent>
            </Tabs>
            
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <h4 className="font-medium text-amber-800 mb-1">Per-Invite Overrides</h4>
              <p className="text-sm text-amber-700">
                These settings are the defaults for all {selectedCategoryData.name} invites. 
                When creating an invite, you can override any field. Pilots can also customize their own labels.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
